import React, { useEffect, useRef, useState } from 'react';
import { Bookmark, CalendarDays, Check, ChevronDown, ChevronUp, ChevronsDownUp, ChevronsUpDown, Clapperboard, Clock3, Crown, Drama, Heart, Layers, Lock, MonitorPlay, PanelLeftClose, SlidersHorizontal, Star, Tv, Users, X } from 'lucide-react';
import {
  AdvancedControls,
  FloatingTooltip,
  GenreDropdown,
  HideWatchingToggle,
  RatingCountFilter,
  RatingRange,
  ReelArt,
  ServiceTiles,
  UnwatchedToggle,
  YearRange,
  useFavoriteServices,
} from './FilterControls.jsx';
import { isSameFilters, SavedFiltersSection } from './SavedFilters.jsx';
import { CIRCLE_FILTERS, DEFAULT_FILTERS, activeFilterChips, availabilityMode, ratingLabel, sectionSummaries, servicesShowing } from '../filters.js';
import { nextServiceSelection } from '@/lib/serviceSelect';

// One colour per section (DECISIONS #21) — muted so they identify a section
// without competing with gold, which is reserved for "selected/filtering".
// Streaming Services keeps gold as its own colour; Rating uses amber-orange.
const SECTION_ACCENTS = {
  saved: '#c8ccd6', // silver
  type: '#7c93b8', // slate blue
  where: '#3fb6c6', // teal
  genres: '#9b7fd1', // purple
  social: '#d06a8a', // rose
  services: '#f6c026', // gold
  year: '#5c9ccf', // steel blue
  rating: '#f0893c', // amber-orange
  activity: '#7d8ae0', // periwinkle (distinct from Release Year's blue)
  advanced: '#4fb38a', // green
};

// For a hover glow in each card's own colour (DECISIONS #22) — Tailwind's
// `hover:shadow-[...]` only accepts a static value per class, not a prop, so
// the accent is threaded through as an `r,g,b` CSS variable and the shadow's
// arbitrary value reads it back with `rgba(var(--accent-rgb),alpha)`.
function hexToRgbTriplet(hex) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return m ? `${parseInt(m[1], 16)},${parseInt(m[2], 16)},${parseInt(m[3], 16)}` : '246,192,38';
}

// Streaming Services open, everything else collapsed — each header's summary
// says what's set, so members rarely need to open the others (DECISIONS #18).
const DEFAULT_OPEN = { saved: false, type: false, where: false, genres: false, social: false, services: false, year: false, rating: false, activity: false, advanced: false };

// Open/collapsed sections (DECISIONS #22): every visit to Screening Room
// starts with ALL sections collapsed — the header summaries say what's set.
// No longer saved on the account. One module-level copy keeps the sidebar
// and the mobile drawer in step during a visit; ScreeningRoomBrowse calls
// resetFilterSections() when the page opens, and AuthContext still calls
// resetFilterSectionsCache() on sign-in/out.
let sectionsStore = null;
const sectionsListeners = new Set();
function setSectionsStore(next) {
  sectionsStore = next;
  sectionsListeners.forEach((fn) => fn(next ?? DEFAULT_OPEN));
}

export function resetFilterSections() {
  setSectionsStore(null);
}
// Kept under its old name for AuthContext's existing calls.
export const resetFilterSectionsCache = resetFilterSections;

function useFilterSections() {
  const [open, setLocal] = useState(sectionsStore ?? DEFAULT_OPEN);
  useEffect(() => {
    setLocal(sectionsStore ?? DEFAULT_OPEN);
    sectionsListeners.add(setLocal);
    return () => sectionsListeners.delete(setLocal);
  }, []);
  const flip = (k) => {
    const cur = sectionsStore ?? DEFAULT_OPEN;
    setSectionsStore({ ...cur, [k]: !cur[k] });
  };
  const setAll = (value) => setSectionsStore(Object.fromEntries(Object.keys(DEFAULT_OPEN).map((k) => [k, value])));
  return [open, flip, setAll];
}


/** Two-up pill toggles (Content Type, Where to Watch). */
function PillToggle({ on, onClick, icon: Icon, children, testid }) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      data-testid={testid}
      className={`flex min-h-[32px] min-w-0 flex-1 items-center justify-center gap-[7px] rounded-[8px] border px-[10px] py-[6px] text-[13.5px] font-semibold leading-tight transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sr-gold ${
        on ? 'border-sr-gold bg-sr-gold/[.12] text-sr-text' : 'border-sr-tile-line text-sr-soft hover:border-sr-muted'
      }`}
    >
      {Icon && <Icon size={15} className={`shrink-0 ${on ? 'text-sr-gold' : 'text-sr-muted'}`} aria-hidden />}
      <span className="min-w-0 text-center">{children}</span>
      {on && <Check size={14} strokeWidth={3} className="shrink-0 text-sr-gold" aria-hidden />}
    </button>
  );
}

/** Friends & Social as two cards — gating is unchanged (`gate()`), only the look. */
const CIRCLE_CARD_META = {
  circleRatedHigh: { icon: Heart, title: 'Loved by Friends', sub: 'A friend rated it 8+ or “Worth it”' },
  circleWatching: { icon: Users, title: 'Friends Watching Now', sub: 'A friend is watching it right now' },
};
function CircleCards({ filters, patch, gate, onLocked }) {
  return (
    <div className="grid grid-cols-2 gap-[8px]" data-testid="sr-circle-cards">
      {CIRCLE_FILTERS.map((c) => {
        const meta = CIRCLE_CARD_META[c.id];
        const state = gate(c.id);
        const on = !!filters[c.id];
        const Icon = meta.icon;
        return (
          <button
            key={c.id}
            type="button"
            aria-pressed={on}
            disabled={state.disabled}
            title={state.reason ?? c.hint}
            onClick={() => (state.locked ? onLocked?.() : patch({ [c.id]: !on }))}
            data-testid={`sr-circle-card-${c.id}`}
            className={`relative flex min-h-[78px] flex-col items-center justify-center gap-[3px] rounded-[9px] border px-[6px] py-[8px] text-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sr-gold disabled:cursor-not-allowed disabled:opacity-45 ${
              on ? 'border-sr-gold bg-sr-gold/[.12]' : 'border-sr-tile-line hover:border-sr-muted'
            }`}
          >
            {on && (
              <span aria-hidden className="absolute right-[5px] top-[5px] grid h-[16px] w-[16px] place-items-center rounded-full bg-sr-gold text-sr-bg">
                <Check size={10} strokeWidth={3} />
              </span>
            )}
            {state.locked && <Lock size={12} className="absolute right-[6px] top-[6px] text-sr-gold" aria-hidden />}
            <Icon size={18} className={on ? 'text-sr-gold' : 'text-sr-soft'} aria-hidden />
            <span className="text-[12px] font-semibold leading-tight text-sr-text">{meta.title}</span>
            <span className="text-[10.5px] leading-tight text-sr-muted">{state.disabled ? state.reason : meta.sub}</span>
          </button>
        );
      })}
    </div>
  );
}

/** "Active Filters (n)" — every non-default filter as a removable chip. */
function ActiveFiltersBar({ chips }) {
  const [expanded, setExpanded] = useState(true);
  if (!chips.length) return null;
  return (
    <div data-testid="sr-active-filters" className="my-[10px] rounded-[10px] border border-sr-gold/40 bg-sr-gold/[.05] px-[10px] py-[8px]">
      <div className="flex items-center justify-between gap-[8px]">
        <p className="m-0 text-[14px] font-semibold text-sr-gold">Active Filters ({chips.length})</p>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-label={expanded ? 'Hide active filters' : 'Show active filters'}
          aria-expanded={expanded}
          data-testid="sr-active-filters-toggle"
          className="shrink-0 text-sr-soft hover:text-sr-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sr-gold"
        >
          {expanded ? <ChevronUp size={16} aria-hidden /> : <ChevronDown size={16} aria-hidden />}
        </button>
      </div>
      {expanded && (
        <div className="mt-[6px] flex flex-wrap gap-[5px]">
          {chips.map((c) => (
            <button
              key={c.key}
              type="button"
              onClick={c.clear}
              aria-label={`Remove filter ${c.label}`}
              data-testid={`sr-active-chip-${c.key}`}
              className="inline-flex max-w-full items-center gap-[4px] rounded-full border border-sr-gold/50 px-[8px] py-[1px] text-[12px] text-sr-text hover:border-sr-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sr-gold"
            >
              <span className="truncate">{c.label}</span>
              <X size={11} strokeWidth={2.6} className="shrink-0 text-sr-muted" aria-hidden />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Section({ id, icon: Icon, title, summary, open, onToggle, children, accent = '#8a94a6', active = false, preview, contentClass = 'px-[14px] pb-[12px]' }) {
  const panelId = `sr-filter-${id}`;
  const sectionRef = useRef(null);
  // Sections live inside the sidebar's own internal scroll (see the
  // `overflow-y-auto` wrapper in ScreeningRoom.jsx). Expanding a section
  // near the bottom (e.g. "Advanced") renders its content *below* whatever
  // is currently scrolled into view, with nothing to auto-scroll it into
  // sight — it looked like the click "did nothing". Only act on a real
  // false -> true transition of `open` (a ref that merely flags "mount
  // already happened" gets fooled by StrictMode's dev-only double-invoke
  // of mount effects, which replays this effect a second time after its
  // own cleanup and would otherwise treat already-open sections as freshly
  // opened on page load).
  // Only when the member tapped THIS header — never when sections open for
  // any other reason (Expand all, state arriving), which used to pull the
  // page down into the filters on load (DECISIONS #22).
  const tappedRef = useRef(false);
  const prevOpenRef = useRef(open);
  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = open;
    const tapped = tappedRef.current;
    tappedRef.current = false;
    if (!open || wasOpen || !tapped) return undefined;
    const raf = requestAnimationFrame(() => {
      const node = sectionRef.current;
      if (!node) return;
      // Scroll only the sidebar's own internal scroller — never the
      // window or any other ancestor — by walking up to the nearest
      // element that actually overflows.
      let scroller = node.parentElement;
      while (scroller && scroller !== document.body && scroller.scrollHeight <= scroller.clientHeight + 1) {
        scroller = scroller.parentElement;
      }
      if (!scroller || scroller === document.body) return;
      const nodeRect = node.getBoundingClientRect();
      const scrollerRect = scroller.getBoundingClientRect();
      const overflowBelow = nodeRect.bottom - scrollerRect.bottom;
      const overflowAbove = scrollerRect.top - nodeRect.top;
      if (overflowBelow > 0) scroller.scrollBy({ top: overflowBelow + 8, behavior: 'smooth' });
      else if (overflowAbove > 0) scroller.scrollBy({ top: -(overflowAbove + 8), behavior: 'smooth' });
    });
    return () => cancelAnimationFrame(raf);
  }, [open]);
  return (
    // Each section is its own card (DECISIONS #21): a colour accent bar and
    // icon tile identify it; a GOLD border means "this section is filtering
    // right now" (gold keeps meaning "selected" everywhere in the app).
    // Expanding a section on its own never adds the gold border.
    <section
      ref={sectionRef}
      data-testid={`sr-section-card-${id}`}
      style={{ '--accent-rgb': hexToRgbTriplet(accent) }}
      className={`relative mb-[10px] overflow-hidden rounded-[11px] border bg-sr-tile transition-[transform,box-shadow,border-color] duration-200 ease-out hover:-translate-y-[2px] motion-reduce:hover:translate-y-0 last:mb-0 ${
        active
          ? 'border-sr-gold shadow-[0_0_0_1px_rgba(246,192,38,0.35),0_0_14px_rgba(246,192,38,0.18)] hover:shadow-[0_0_0_1px_rgba(246,192,38,0.5),0_12px_26px_-8px_rgba(246,192,38,0.4)]'
          : 'border-sr-tile-line hover:border-[rgba(var(--accent-rgb),0.55)] hover:shadow-[0_12px_26px_-10px_rgba(var(--accent-rgb),0.45)]'
      }`}
    >
      <span aria-hidden className="absolute inset-y-0 left-0 w-[4px]" style={{ backgroundColor: accent }} />
      <h3 className="m-0">
        <button
          type="button"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => {
            tappedRef.current = true;
            onToggle();
          }}
          data-testid={`sr-section-toggle-${id}`}
          className="flex min-h-[38px] w-full items-center gap-[12px] py-[10px] pl-[14px] pr-[12px] text-left text-[16px] font-semibold text-sr-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sr-gold"
        >
          <span aria-hidden className="grid h-[36px] w-[36px] shrink-0 place-items-center rounded-[9px]" style={{ backgroundColor: `${accent}3d`, color: accent, boxShadow: `inset 0 0 0 1px ${accent}59` }}>
            <Icon size={19} strokeWidth={1.7} aria-hidden />
          </span>
          {/* Title + summary stacked, not beside each other — a long section
              name (e.g. "Streaming Services") left almost no room for the
              summary on the same line, breaking it mid-word. */}
          <span className="min-w-0 flex-1">
            <span className="block truncate">{title}</span>
            {summary && (
              <span data-testid={`sr-section-summary-${id}`} className="line-clamp-2 block text-left text-[12.5px] font-normal leading-tight text-sr-soft">
                {summary}
              </span>
            )}
          </span>
          {open ? <ChevronUp size={18} className="ml-auto shrink-0 self-start" aria-hidden /> : <ChevronDown size={18} className="ml-auto shrink-0 self-start" aria-hidden />}
        </button>
      </h3>
      {open ? (
        <div id={panelId} className={contentClass}>
          {children}
        </div>
      ) : (
        preview && <div className="px-[14px] pb-[12px]">{preview}</div>
      )}
    </section>
  );
}

/**
 * The filter sidebar (desktop) — also rendered inside the mobile drawer.
 * `api` is the object built in ScreeningRoom.jsx: { filters, patch, toggle, gate, ... }.
 */
export default function FilterPanel({ api, className = '', onCollapse, collapseButtonRef }) {
  const { filters, patch, toggle, gate, genres, providers, majorProviders, languages, renderProviderBadge, userProviderIds, onManageServices, onUnpinMyService, onApplyServices, onLocked, clearAll, availabilityFilter } = api;
  const [open, flip, setAllSections] = useFilterSections();
  const allOpen = Object.values(open).every(Boolean);
  const savedList = api.savedFilters?.list || [];
  const savedLocked = Boolean(api.savedFilters?.locked);
  const savedInUse = savedLocked ? null : savedList.find((s) => isSameFilters(s.filters, api.savedFilters.current));
  const savedSummary = savedLocked
    ? 'Premiere Only'
    : savedInUse
    ? `Using “${savedInUse.name}”`
    : savedList.length
      ? `${savedList.length} saved`
      : 'None saved yet';
  const theatersOnly = availabilityMode(filters) === 'theaters';
  const services = servicesShowing({ enabled: availabilityFilter.enabled, selectedIds: filters.providers, userProviderIds, providers });
  const summary = sectionSummaries(filters, { genres, languages, services });
  const chips = activeFilterChips(filters, {
    genres,
    languages,
    services,
    actions: {
      patch,
      clearServices: () => {
        if (availabilityFilter.enabled) availabilityFilter.setEnabled?.(false);
        patch({ providers: [] });
      },
    },
  });
  // Content Type: [] means both. Tapping a pill turns that type off/on; if
  // that would leave none (or both), it goes back to [] = Movies + TV.
  const typeOn = (id) => !filters.types.length || filters.types.includes(id);
  const flipType = (id) => {
    const cur = filters.types.length ? filters.types : ['movie', 'tv'];
    const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
    patch({ types: next.length === 1 ? next : [] });
  };
  const { favorites } = useFavoriteServices();
  const favoriteCount = favorites.filter((id) => !userProviderIds.includes(id)).length;
  // A section is "active" (gold border) when it holds any non-default filter.
  const activeSections = {
    type: filters.types.length === 1,
    where: availabilityMode(filters) !== '',
    genres: filters.genres.length > 0,
    social: CIRCLE_FILTERS.some((c) => filters[c.id]),
    services: !theatersOnly && Boolean(services.base || services.extras.length),
    year: Boolean(filters.yearFrom || filters.yearTo),
    rating: Boolean(ratingLabel(filters)) || filters.minVotes !== DEFAULT_FILTERS.minVotes,
    activity: Boolean(filters.unwatched || filters.hideWatching),
    advanced: filters.runtime !== 'any' || Boolean(filters.language) || filters.ageMax !== 'any' || Boolean(filters.releasedOnly),
  };
  // Collapsed Streaming Services still shows what's showing plus a three-part
  // status line, so members can see their setup without opening it. Tapping
  // anywhere on it opens the section.
  const myServicesStatus = !availabilityFilter.isPremium
    ? 'Premiere Only'
    : !userProviderIds.length
      ? 'Add subscriptions'
      : availabilityFilter.enabled
        ? `On (${userProviderIds.length})`
        : 'Off';
  const servicesPreview = theatersOnly
    ? null // the collapsed header's own summary already reads "Not used" — repeating "Not used for In Theaters" right below it was redundant.
    : (
    <button type="button" onClick={() => flip('services')} data-testid="sr-services-preview" className="block w-full text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sr-gold">
      <div className="relative overflow-hidden rounded-[9px] border border-sr-gold/45 bg-sr-gold/[.06] px-[12px] py-[9px]">
        <ReelArt />
        <p className="relative m-0 text-[13px] font-semibold text-sr-text">
          Currently Showing <span className="font-normal text-[#f3dc9a]">{services.label}</span>
        </p>
      </div>
      {/* Icon + value only (no "My Services"/"Subscriptions"/"Favorites"
          label text) — 3 columns in ~280px had no room for a label AND its
          value ("My Services On (3)" was cut to "My Services …", hiding the
          one thing the preview exists to show). The label is still there
          as a tooltip on hover/focus. */}
      <div className="mt-[7px] flex items-stretch gap-[6px]">
        {[
          { icon: Crown, label: 'My Services', value: myServicesStatus, gold: availabilityFilter.isPremium && availabilityFilter.enabled },
          { icon: Layers, label: 'Subscriptions', value: String(userProviderIds.length), gold: true },
          { icon: Heart, label: 'Favorites', value: String(favoriteCount), gold: true },
        ].map(({ icon: I, label, value, gold }) => (
          <span key={label} title={label} className="flex min-w-0 flex-1 flex-col items-center gap-[3px] rounded-[7px] bg-black/15 px-[4px] py-[6px] text-center">
            <I size={13} className={gold ? 'text-sr-gold' : 'text-sr-muted'} aria-hidden />
            {/* `title` only ever shows on mouse hover — touch and screen-
                reader users need the label some other way, so it's also
                read (but not shown) right before the value. */}
            <span className="line-clamp-2 text-[10.5px] font-semibold leading-tight text-sr-soft">
              <span className="sr-only">{label}: </span>
              {value}
            </span>
          </span>
        ))}
      </div>
    </button>
  );
  const localCollapseRef = useRef(null);
  const collapseRef = collapseButtonRef ?? localCollapseRef;
  const [collapseHinted, setCollapseHinted] = useState(false);

  return (
    <aside
      aria-label="Filters"
      className={`rounded-[14px] border border-sr-panel-line bg-sr-panel px-[15px] pb-[3px] pr-[11px] ${className}`}
    >
      {/* Sticky within the panel's own scroll (see className above) so Clear
          All and the collapse button stay reachable no matter how far a
          member has scrolled down into the filter sections below. */}
      <div className="sticky top-0 z-10 flex h-[52px] items-center justify-between border-b border-sr-divider bg-sr-panel pl-[4px] pr-[4px]">
        <h2 className="m-0 flex items-center gap-[14px] text-[20px] font-bold text-sr-text">
          <SlidersHorizontal size={23} strokeWidth={1.8} aria-hidden /> Filters
        </h2>
        <div className="flex items-center gap-[10px]">
          <button
            type="button"
            onClick={() => setAllSections(!allOpen)}
            data-testid="sr-sections-toggle-all"
            aria-label={allOpen ? 'Collapse all filter sections' : 'Expand all filter sections'}
            className="flex items-center gap-[4px] rounded text-[13px] text-sr-soft hover:text-sr-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sr-gold"
          >
            {allOpen ? <ChevronsDownUp size={15} aria-hidden /> : <ChevronsUpDown size={15} aria-hidden />}
            {allOpen ? 'Collapse all' : 'Expand all'}
          </button>
          <button type="button" onClick={clearAll} className="rounded text-[14px] text-sr-soft hover:text-sr-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sr-gold">
            Clear All
          </button>
          {onCollapse && (
            <div className="relative">
              <button
                type="button"
                ref={collapseRef}
                onClick={onCollapse}
                onMouseEnter={() => setCollapseHinted(true)}
                onMouseLeave={() => setCollapseHinted(false)}
                onFocus={(e) => setCollapseHinted(e.target.matches(':focus-visible'))}
                onBlur={() => setCollapseHinted(false)}
                aria-label="Hide filters"
                aria-expanded={true}
                data-testid="sr-sidebar-collapse-button"
                className="grid h-[34px] w-[34px] place-items-center rounded-[9px] border border-transparent text-sr-soft hover:border-sr-gold hover:text-sr-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sr-gold"
              >
                <PanelLeftClose size={21} aria-hidden />
              </button>
              <FloatingTooltip anchorRef={collapseRef} visible={collapseHinted}>
                Collapse
              </FloatingTooltip>
            </div>
          )}
        </div>
      </div>

      <ActiveFiltersBar chips={chips} />

      {api.savedFilters && (
        <Section
          id="saved"
          icon={Bookmark}
          title="Saved Filters"
          summary={savedSummary}
          open={open.saved}
          onToggle={() => flip('saved')}
          accent={SECTION_ACCENTS.saved}
          active={Boolean(savedInUse)}
        >
          <SavedFiltersSection saved={api.savedFilters} genres={genres} providers={providers} languages={languages} hasFilters={chips.length > 0} onLocked={onLocked} />
        </Section>
      )}

      <Section id="type" icon={Clapperboard} title="Content Type" summary={summary.type} open={open.type} onToggle={() => flip('type')} accent={SECTION_ACCENTS.type} active={activeSections.type} contentClass="px-[14px] pb-[12px]">
        <div className="flex gap-[8px]">
          <PillToggle on={typeOn('movie')} onClick={() => flipType('movie')} testid="sr-type-movie">Movies</PillToggle>
          <PillToggle on={typeOn('tv')} onClick={() => flipType('tv')} testid="sr-type-tv">TV Series</PillToggle>
        </div>
      </Section>

      <Section id="where" icon={MonitorPlay} title="Where to Watch" summary={summary.where} open={open.where} onToggle={() => flip('where')} accent={SECTION_ACCENTS.where} active={activeSections.where} contentClass="px-[14px] pb-[12px]">
        <div className="flex gap-[8px]" data-testid="sr-availability-control">
          <PillToggle on={!!filters.atHome} onClick={() => patch({ atHome: !filters.atHome })} icon={MonitorPlay} testid="sr-avail-home-checkbox">
            Streaming at Home
          </PillToggle>
          <PillToggle on={!!filters.inTheaters} onClick={() => patch({ inTheaters: !filters.inTheaters })} icon={Clapperboard} testid="sr-avail-theaters-checkbox">
            In Theaters
          </PillToggle>
        </div>
        {theatersOnly && <p className="m-0 mt-[6px] text-[12px] leading-snug text-sr-muted">Movies only · released in the last 45 days</p>}
      </Section>

      <Section id="genres" icon={Drama} title="Genres" summary={summary.genres} open={open.genres} onToggle={() => flip('genres')} accent={SECTION_ACCENTS.genres} active={activeSections.genres} contentClass="px-[14px] pb-[12px]">
        <GenreDropdown genres={genres} selected={filters.genres} onToggle={(id) => toggle('genres', id)} onClear={() => patch({ genres: [] })} placeholder={filters.genres.length ? '+ Add more genres' : 'Select genres…'} />
        {filters.genres.length > 0 && (
          <div className="mt-[8px] flex flex-wrap gap-[6px]" data-testid="sr-genre-chips">
            {filters.genres.map((id) => {
              const name = genres.find((g) => g.id === id)?.name;
              if (!name) return null;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => toggle('genres', id)}
                  aria-label={`Remove genre ${name}`}
                  className="inline-flex items-center gap-[5px] rounded-full border border-sr-gold/60 px-[9px] py-[2px] text-[12.5px] text-sr-text hover:border-sr-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sr-gold"
                >
                  {name}
                  <X size={11} strokeWidth={2.6} className="text-sr-muted" aria-hidden />
                </button>
              );
            })}
          </div>
        )}
      </Section>

      <Section id="social" icon={Users} title="Friends & Social" summary={summary.social} open={open.social} onToggle={() => flip('social')} accent={SECTION_ACCENTS.social} active={activeSections.social} contentClass="px-[14px] pb-[12px]">
        <CircleCards filters={filters} patch={patch} gate={gate} onLocked={onLocked} />
      </Section>

      <Section
        id="services"
        icon={Tv}
        title="Streaming Services"
        summary={summary.services}
        open={open.services}
        onToggle={() => flip('services')}
        accent={SECTION_ACCENTS.services}
        active={activeSections.services}
        preview={servicesPreview}
        contentClass="px-[14px] pb-[14px]"
      >
        <ServiceTiles
          major={majorProviders}
          all={providers}
          userProviderIds={userProviderIds}
          onManageServices={onManageServices}
          selected={filters.providers}
          onToggle={(id) =>
            availabilityFilter.isPremium
              ? toggle('providers', id)
              : patch({ providers: nextServiceSelection(filters.providers, id, false) })
          }
          renderProviderBadge={renderProviderBadge}
          availabilityFilter={availabilityFilter}
          onUnpinMyService={onUnpinMyService}
          onSetProviders={(ids) => patch({ providers: ids })}
          onApplyServices={onApplyServices}
          onLocked={onLocked}
          theatersOnly={theatersOnly}
        />
      </Section>

      <Section id="year" icon={CalendarDays} title="Release Year" summary={summary.year} open={open.year} onToggle={() => flip('year')} accent={SECTION_ACCENTS.year} active={activeSections.year} contentClass="px-[14px] pb-[12px]">
        <YearRange yearFrom={filters.yearFrom} yearTo={filters.yearTo} onChange={patch} />
      </Section>

      <Section id="rating" icon={Star} title="Rating" summary={summary.rating} open={open.rating} onToggle={() => flip('rating')} accent={SECTION_ACCENTS.rating} active={activeSections.rating} contentClass="px-[14px] pb-[14px]">
        <RatingRange ratingMin={filters.ratingMin} onChange={patch} />
        <div className="mt-[14px]">
          <RatingCountFilter filters={filters} patch={patch} gate={gate} onLocked={onLocked} />
        </div>
      </Section>

      <Section id="activity" icon={Clock3} title="My Activity" summary={summary.activity} open={open.activity} onToggle={() => flip('activity')} accent={SECTION_ACCENTS.activity} active={activeSections.activity}>
        <UnwatchedToggle filters={filters} patch={patch} gate={gate} onLocked={onLocked} />
        <HideWatchingToggle filters={filters} patch={patch} gate={gate} onLocked={onLocked} />
      </Section>

      <Section id="advanced" icon={SlidersHorizontal} title="Advanced" summary={summary.advanced} open={open.advanced} onToggle={() => flip('advanced')} accent={SECTION_ACCENTS.advanced} active={activeSections.advanced} contentClass="px-[14px] pb-[12px]">
        <AdvancedControls filters={filters} patch={patch} gate={gate} onLocked={onLocked} languages={languages} />
      </Section>
    </aside>
  );
}
